import { useContext, useState } from "react";
import { WretchResponse } from "wretch/types";
import NotificationDispatch, {
  showErrorNotification,
  showInformativeNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import useToggle from "../../hooks/useToggle";
import { SP } from "../../http/serviceportalApi";
import { useTranslations } from "../../hooks/useTranslations";
import { downloadFile } from "../../utils";
import Button from "../ui/Button";
import ModalDialog from "../ui/ModalDialog";
import styles from "./DocumentsDownload.module.css";

interface Props {
  links: SP.DownloadLinks;
  fetchDocument: (link: string) => Promise<WretchResponse>;
  onDownloadSuccess: () => void;
}

const DocumentsDownload = ({
  links,
  fetchDocument,
  onDownloadSuccess,
}: Props) => {
  const { i18n } = useTranslations();
  const dispatch = useContext(NotificationDispatch);
  const modal = useToggle();
  const [downloadsInProgress, setDownloadsInProgress] = useState(0);

  const downloadDocument = async (link: string) => {
    const res = await fetchDocument(link);
    switch (res.headers.get("Content-Type")) {
      case "application/pdf":
      case "application/xml":
      case "application/zip":
      case "text/plain":
      case "text/csv":
      case "application/octet-stream":
        await downloadFile(res);
        break;
      default:
        throw new Error("Unknown content type");
    }
  };

  const onDownloadAllClick = async (link: string | null) => {
    if (!link) return;
    setDownloadsInProgress(downloadsInProgress + 1);
    modal.close();
    dispatch(showInformativeNotification(i18n.documents.download.started));
    return downloadDocument(link)
      .then(() => {
        dispatch(showSuccessNotification(i18n.documents.download.success));
        onDownloadSuccess();
      })
      .catch((error) =>
        dispatch(showErrorNotification(error, i18n.documents.download.error)),
      )
      .finally(() => setDownloadsInProgress(downloadsInProgress - 1));
  };

  return (
    <>
      <Button
        isLoading={downloadsInProgress > 0}
        buttonProps={{
          className: styles.downloadButton,
          disabled: Object.values(links).every((l) => !l),
          onClick: modal.toggle,
        }}
      >
        {i18n.documents.download.downloadAll}
      </Button>

      <ModalDialog
        isOpen={modal.isOpen}
        onClose={modal.close}
        title={i18n.documents.download.downloadAll}
      >
        <div className={styles.modalContent}>
          {i18n.documents.download.formatPrompt}
          <div className={styles.formatButtons}>
            {links.zip && (
              <DownloadButton
                title={i18n.documents.download.formats.zip.label}
                tooltip={i18n.documents.download.formats.zip.description}
                onClick={() => onDownloadAllClick(links.zip)}
              />
            )}

            {links.pdf && (
              <DownloadButton
                title={i18n.documents.download.formats.pdf.label}
                tooltip={i18n.documents.download.formats.pdf.description}
                onClick={() => onDownloadAllClick(links.pdf)}
              />
            )}

            {links.pdfWithoutMyGastroDocx && (
              <DownloadButton
                title={
                  i18n.documents.download.formats.pdfWithoutMyGastroDocx.label
                }
                tooltip={
                  i18n.documents.download.formats.pdfWithoutMyGastroDocx
                    .description
                }
                onClick={() => onDownloadAllClick(links.pdfWithoutMyGastroDocx)}
              />
            )}
          </div>
        </div>
      </ModalDialog>
    </>
  );
};

interface DownloadButtonProps {
  title: string;
  tooltip: string;
  onClick: () => void;
}

const DownloadButton = ({ title, tooltip, onClick }: DownloadButtonProps) => (
  <button className={styles.formatButton} title={tooltip} onClick={onClick}>
    {title}
  </button>
);

export default DocumentsDownload;
